import { useMemo } from 'react';
import { SalesforceProduct, SalesforceProductPromotionType } from '~/lib/salesforce/types';

/** Promotions that have a campaign page of their own to send the customer to. */
const campaignsWithRedirects: string[] = [
    SalesforceProductPromotionType.MIX_AND_MATCH,
    SalesforceProductPromotionType.BASTARD,
    SalesforceProductPromotionType.BUNDLE,
];

/** Only these promotions have a campaign page to link to. `OF-` is used for ImercoPrice. */
const REDIRECTABLE_PROMOTION_PREFIXES = ['HOOF-', 'OF-'];

export interface RedirectableCampaign {
    type: string;
    /** Only set when the id can address a campaign page, so it doubles as 'is there a link'. */
    promotionId?: string;
}

/**
 * Whether a campaign should take over how the product presents its price and offer a way to its
 * own page. The old PDP asked this in two slightly different ways and both are kept: any
 * promotion identity flattens the price list, while only a prefixed id can produce a link.
 *
 * Takes the product rather than reading the page's, since recommendations run it on theirs.
 */
export const useRedirectableCampaign = (product: SalesforceProduct): RedirectableCampaign | null =>
    useMemo(() => {
        const type = product.c_activepromo?.type?.toLowerCase();

        if (!type || !campaignsWithRedirects.includes(type)) return null;

        const promotionId = product.c_activepromo?.promotionid;
        const isRedirectableId =
            !!promotionId &&
            REDIRECTABLE_PROMOTION_PREFIXES.some((prefix) => promotionId.startsWith(prefix));

        if (!isRedirectableId && !product.c_specialPrices?.id) return null;

        return { type, promotionId: isRedirectableId ? promotionId : undefined };
    }, [product]);
